import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import LoginPage from './pages/LoginPage';
import AssistantWidget from './components/AssistantWidget';
import Sidebar from './components/Sidebar';
import PageRouter from './components/PageRouter';
import { useNotificationCount } from './hooks/useNotificationCount';
import { Menu } from 'lucide-react';
import LandingPage from './landing/LandingPage';
import TradingHubApp from './trading/TradingHubApp';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500 font-medium">Caricamento EDILCIMA...</p>
      </div>
    </div>
  );
}

function MobileHeader({ onOpenSidebar }) {
  return (
    <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 lg:hidden">
      <button onClick={onOpenSidebar} className="p-2 hover:bg-slate-100 rounded-md" data-testid="mobile-menu">
        <Menu size={20} />
      </button>
      <div className="flex items-center gap-2">
        <img src="/edilcima_logo.png" alt="EDIL CIMA SRL" className="w-8 h-8 rounded object-contain bg-slate-900 p-0.5" />
        <span className="font-black text-sm">EDIL CIMA S.R.L.</span>
      </div>
    </header>
  );
}

function GestionaleApp() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState('dashboard');
  const [pageContext, setPageContext] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const notifCount = useNotificationCount(user);

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/app/login" replace />;

  const onNavigate = (p, ctx) => {
    setPage(p);
    setPageContext(ctx || null);
    setMobileSidebar(false);
  };

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex bg-surface" data-testid="app-layout">
      <Sidebar
        user={user}
        page={page}
        sidebarOpen={sidebarOpen}
        mobileSidebar={mobileSidebar}
        notifCount={notifCount}
        onNavigate={onNavigate}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onLogout={onLogout}
        onCloseMobile={() => setMobileSidebar(false)}
      />
      <main className="flex-1 min-h-screen flex flex-col">
        <MobileHeader onOpenSidebar={() => setMobileSidebar(true)} />
        <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <PageRouter page={page} pageContext={pageContext} user={user} onNavigate={onNavigate} />
        </div>
      </main>
      <AssistantWidget />
    </div>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/app" replace />;
  return <LoginPage />;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/app/login" element={<LoginRoute />} />
              <Route path="/app/*" element={<GestionaleApp />} />
              {/* Trading Hub (area riservata, non linkata da nessuna parte) */}
              <Route path="/th-portal/*" element={<TradingHubApp />} />
              {/* Backward compatibility: old direct /login still works */}
              <Route path="/login" element={<Navigate to="/app/login" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ConfirmProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
